import React, { useState } from 'react'

/* ─────────────────────────────────────────────────────────────
   Blog
   Per brief:
   - Simple editorial layout
   - No comment sections
   - No social sharing buttons
   - No engagement features of any kind
   - Accessible from sidebar within same page experience
   Replace MOCK_POSTS with API data when backend is ready
───────────────────────────────────────────────────────────── */

const MOCK_POSTS = [
  {
    id: 1,
    category: 'Insight',
    title: 'The Art of the Quiet Arrival',
    excerpt: 'What separates a seamless airport experience from a merely efficient one is not speed — it is the quality of anticipation. A note on how we approach every departure and arrival for our members.',
    date: '18 April 2026',
    readTime: '4 min',
    body: `There is a particular kind of stress that accompanies air travel — not the fear of flying, but the accumulation of small uncertainties. Will the car be on time? Will the lounge be accessible? Will someone be waiting at the gate?

For the members we serve, these uncertainties are not merely inconvenient. They represent a gap between the standard expected and the standard received.

Our approach to airport coordination begins long before the aircraft lands. We confirm ground arrangements with the handling agent the evening prior. We track the flight in real time. Our representative is present not when the aircraft lands, but when it is approaching.

The quiet arrival is not an absence of service — it is its highest expression. The absence of friction, the absence of uncertainty, the presence of someone who already knows what is needed.

This is what we mean when we say we facilitate. Not simply to arrange, but to anticipate. To ensure that the space between one place and the next is, for our members, as seamless as everything that follows.`,
  },
  {
    id: 2,
    category: 'Travel',
    title: 'Brussels in Spring: A Private View',
    excerpt: 'For those who call Brussels home — or make it so for a season — the city reveals itself slowly. The institutional gravity of the EU quarter gives way, in April, to something far more intimate.',
    date: '11 April 2026',
    readTime: '6 min',
    body: `Brussels resists easy characterisation. It is not Paris, though the architecture occasionally gestures in that direction. It is not Amsterdam, though the canals suggest a shared northern heritage. It is, most accurately, itself — quietly cosmopolitan, confidently understated, and almost pathologically reluctant to advertise.

In spring, the city opens. The Parc du Cinquantenaire becomes passable again without an overcoat. The terraces of the Châtelain neighbourhood fill at an hour that would, elsewhere, be considered premature. The Sablon antique market recovers its weekend purpose.

For members stationed in Brussels — at one of the city's many embassies or permanent missions — this seasonal shift is particularly pronounced. The city's diplomatic life accelerates in parallel with the warming weather, and the demand for seamless ground arrangements rises accordingly.

We have found, over the years, that Brussels rewards the member who looks beyond the obvious. The Stanhope and the Amigo remain the preferred addresses for a reason. But the smaller properties — the Pillows, the Made in Louise — offer a discretion and a quiet that the larger addresses cannot always guarantee.

If you are arriving in Brussels this spring, or if you have guests coming whom you would like to receive well, speak to us. We know the city, and we know what it asks of those who move through it.`,
  },
  {
    id: 3,
    category: 'Aviation',
    title: 'On Cabin Class: Why We Offer Two, Not More',
    excerpt: 'Mwuïlu arranges Business and First Class travel exclusively. This is not a limitation — it is a considered position. A brief explanation of why.',
    date: '03 April 2026',
    readTime: '3 min',
    body: `We are occasionally asked whether we can arrange economy travel. The answer is no, and the reason is straightforward.

The members we serve travel for reasons that do not accommodate compromise. A session in The Hague. A bilateral at an undisclosed location. A family matter that requires immediate presence and complete discretion. The quality of travel is not a luxury consideration in these circumstances — it is a functional one.

Business Class, on the routes and carriers we work with, provides the separation, the quiet, and the flexibility that this kind of travel demands. First Class extends this further, with the addition of privacy suites on long-haul routes that have become, for some of our members, the only acceptable option for certain journeys.

We do not offer economy travel because we cannot offer it well. And we are not in the business of doing things poorly.

When you submit a flight request through this portal, you will find two options: Business and First. If you have a preference for a specific carrier, or a specific configuration on a specific aircraft, tell us. We will do our best to accommodate it. That is what we are here for.`,
  },
]

/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────── */
export default function Blog({ onBack }) {
  const [activePost, setActivePost] = useState(null)

  if (activePost) {
    return (
      <BlogPost
        post={activePost}
        onBack={() => setActivePost(null)}
        onDashboard={onBack}
      />
    )
  }

  return (
    <div className="db-page cnt-page cnt-page--wide">

      {/* Header */}
      <div className="cnt-header">
        <button className="cnt-back" onClick={onBack} aria-label="Back">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Dashboard
        </button>
        <div className="cnt-header__title-wrap">
          <p className="cnt-header__overline">Mwuïlu</p>
          <h1 className="cnt-header__title">Journal</h1>
        </div>
      </div>

      {/* Featured post — first item */}
      <article
        className="cnt-blog-featured"
        onClick={() => setActivePost(MOCK_POSTS[0])}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && setActivePost(MOCK_POSTS[0])}
        aria-label={`Read: ${MOCK_POSTS[0].title}`}
      >
        <div className="cnt-blog-featured__meta">
          <span className="cnt-blog-category">{MOCK_POSTS[0].category}</span>
          <span className="cnt-blog-date">{MOCK_POSTS[0].date}</span>
        </div>
        <h2 className="cnt-blog-featured__title">{MOCK_POSTS[0].title}</h2>
        <p className="cnt-blog-featured__excerpt">{MOCK_POSTS[0].excerpt}</p>
        <span className="cnt-blog-read-more">
          Read {MOCK_POSTS[0].readTime} →
        </span>
      </article>

      {/* Post list */}
      <div className="cnt-blog-list">
        {MOCK_POSTS.slice(1).map((post, i) => (
          <article
            key={post.id}
            className="cnt-blog-row"
            onClick={() => setActivePost(post)}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && setActivePost(post)}
            aria-label={`Read: ${post.title}`}
            style={{ animationDelay: `${(i + 1) * 70}ms` }}
          >
            <div className="cnt-blog-row__meta">
              <span className="cnt-blog-category">{post.category}</span>
              <span className="cnt-blog-date">{post.date}</span>
            </div>
            <div className="cnt-blog-row__right">
              <h3 className="cnt-blog-row__title">{post.title}</h3>
              <p className="cnt-blog-row__excerpt">{post.excerpt}</p>
              <span className="cnt-blog-read-more cnt-blog-read-more--sm">
                {post.readTime} →
              </span>
            </div>
          </article>
        ))}
      </div>

    </div>
  )
}

/* ── Single post view ── */
function BlogPost({ post, onBack, onDashboard }) {
  return (
    <div className="db-page cnt-page cnt-page--article">

      <div className="cnt-header">
        <button className="cnt-back" onClick={onBack} aria-label="Back to journal">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Journal
        </button>
      </div>

      <div className="cnt-article">
        <div className="cnt-article__meta">
          <span className="cnt-blog-category">{post.category}</span>
          <span className="cnt-blog-date">{post.date} · {post.readTime} read</span>
        </div>
        <h1 className="cnt-article__title">{post.title}</h1>
        <p className="cnt-article__excerpt">{post.excerpt}</p>
        <div className="cnt-article__rule" aria-hidden="true" />
        <div className="cnt-article__body">
          {post.body.split('\n\n').map((para, i) => (
            <p key={i} className="cnt-article__para">{para}</p>
          ))}
        </div>
        <div className="cnt-article__sig">— Mwuïlu</div>
      </div>

    </div>
  )
}